<template>
  <div class="h-full flex" @mouseover="isHoveredOver = true" @mouseleave="isHoveredOver = false">
    <div class="w-full relative bg-white">
      <div
        ref="editorContainer"
        class="h-full w-full flex"
        :class="isExpanded ? 'items-stretch' : 'items-start'"
        @click="showLockWarning"
      >
        <FloatingMenu
          v-if="isAiSuggested && !isExpanded"
          placement="bottom"
          :offset="[0, 8]"
          class="h-full"
        >
          <template #activator="{ show }">
            <button
              class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-3 text-gray-800 rounded-md p-2"
              :class="{
                'sticky top-27': expansionPanel?.isShown.value,
                'border border-gray-800': aiSuggestedNotReported,
                'border border-green-500': !aiSuggestedNotReported && isAiSuggested,
              }"
              :title="finalizeButtonTitle"
              @click="show"
            >
              <MinusIcon v-if="isAnswerEmpty" class="w-4" />
              <CheckIcon v-else class="w-4" />
              <SparklesIcon class="w-4" />
            </button>
          </template>
          <template #content="{ hide }">
            <div class="flex flex-col bg-white rounded-lg shadow-lg py-2">
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/accept-suggestion font-medium text-gray-900 flex items-center gap-2"
                @click="acceptAiSuggestion(hide)"
              >
                <CheckIcon class="size-5 group-hover/accept-suggestion:text-green-600" />
                Accept AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/edit-suggestion font-medium text-gray-900 flex items-center gap-2"
                @click="editAiSuggestion(hide)"
              >
                <PencilIcon class="size-4 mr-1 group-hover/edit-suggestion:text-blue-600" />
                Edit AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/reject-suggestion font-medium text-gray-900 flex items-center gap-2"
                @click="rejectAiSuggestion(hide)"
              >
                <XMarkIcon class="size-5 group-hover/reject-suggestion:text-red-600" />
                Reject AI suggestion
              </button>
            </div>
          </template>
        </FloatingMenu>
        <button
          v-else-if="!isAiSuggested && !isExpanded"
          class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-2 rounded-md text-gray-800 p-2"
          :class="{
            'sticky top-27': expansionPanel?.isShown.value,
            'bg-gray-800 text-white': isAnswerEmpty && isFinalized,
            'bg-green-500 text-white': !isAnswerEmpty && isFinalized,
          }"
          :title="finalizeButtonTitle"
          @click="toggleReviewItemSynthesisAttributeFinalization"
        >
          <MinusIcon v-if="isAnswerEmpty" class="w-4" />
          <CheckIcon v-else class="w-4" />
        </button>
        <div
          v-if="!isExpanded"
          class="flex-1 cursor-pointer px-2 py-2"
          @click="expansionPanel?.expand()"
        >
          <div
            v-if="isAnswerEmpty && isFinalized"
            class="flex items-center p-2 border border-black rounded-lg text-sm text-gray-700 bg-gray-100 w-fit"
          >
            Not reported
          </div>
          <template v-else-if="!isAnswerEmpty">
            <StudyDesignThumbnail
              :populations="normalizedStudyDesignValue?.populations ?? []"
              :arms="normalizedStudyDesignValue?.arms ?? []"
              :exposures="normalizedStudyDesignValue?.exposures ?? []"
              :interventions="normalizedStudyDesignValue?.interventions ?? []"
              class="h-48"
            />
          </template>
        </div>
        <div v-if="isExpanded" class="flex flex-col w-full">
          <div
            v-if="isAnswerEmpty && isFinalized"
            class="flex items-center p-2 ml-2 mt-2 border border-black rounded-lg text-sm text-gray-700 bg-gray-100 w-26"
          >
            Not reported
          </div>
          <div v-else-if="isVisible" class="max-h-150">
            <StudyDesignEditor
              :study="study"
              :attribute-structure="attributeStructure"
              :disabled="isFinalized || isAiSuggested || isReviewReadOnly"
            />
          </div>
        </div>
      </div>
      <div v-if="!review.isReviewReadonly.value" class="absolute top-10 -right-4 items-center">
        <div v-if="!isFinalized">
          <Transition name="fade">
            <div v-show="(!review.isReviewReadonly.value && isHoveredOver) || isGeneratingAnswer">
              <StudyQA
                v-model:is-generating-answer="isGeneratingAnswer"
                :study="study"
                :attribute-structure="attributeStructure"
                :query="attributeStructure.question ?? ''"
                :is-review-readonly="review.isReviewReadonly.value"
              />
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import StudyQA from "./StudyQA.vue";
import StudyDesignEditor from "./StudyDesignEditor.vue";
import { StudyDesignThumbnail } from "visual-study-design-builder";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import type { StudyDesignValue } from "@core/domain/types/attributeValue.type";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import useLoading from "@app/composables/use-loading";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { Role } from "@core/domain/types/role.type";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";
import { errorMessage } from "@app/utils/error-message";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { AuthKey } from "@app/injectionKeys";
import { SparklesIcon, CheckIcon, MinusIcon, PencilIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";

const snackbar = useSnackbar();
const loading = useLoading();
const isHoveredOver = ref(false);

const props = withDefaults(
  defineProps<{
    attributeStructure: AttributeStructure;
    study: ReviewItem;
    isExpanded: boolean;
    isReviewReadOnly?: boolean;
    expansionPanel?: any;
  }>(),
  {
    isReviewReadOnly: false,
  },
);

const isGeneratingAnswer = ref(false);
const review = injectStrict(ReviewKey);

const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

const studyDesignValue = computed(
  () => currentAttribute.value?.value as StudyDesignValue | undefined,
);

const normalizedStudyDesignValue = computed(() => {
  const value = studyDesignValue.value;
  if (!value) return undefined;
  return {
    ...value,
    interventions: (value.interventions ?? []).map((i) => ({
      ...i,
      timepoints: i.timepoints ?? [],
    })),
  };
});

const isAnswerEmpty = computed(() => {
  const value = studyDesignValue.value;
  return !value?.populations?.length && !value?.arms?.length && !value?.interventions?.length;
});

const isFinalized = computed(() => currentAttribute.value?.isFinalized ?? false);
const isAiSuggested = computed(() => currentAttribute.value?.isAiSuggested ?? false);
const aiSuggestedNotReported = computed(
  () => currentAttribute.value?.aiSuggestedNotReported ?? false,
);

const auth = injectStrict(AuthKey);

function showLockWarning() {
  if (!review.isReviewReadonly.value) return;
  const isAdmin = auth.user.value?.role === Role.ADMIN;
  let message = "";
  if (review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED && !review.isLocked.value) {
    message = isAdmin
      ? "The plan is unlocked, lock it to make changes."
      : "The plan is unlocked. Please contact an admin to make changes.";
  } else if (review.isLocked.value) {
    message = isAdmin
      ? "The review is locked, unlock it to make changes."
      : "The review is locked. Please contact an admin to make changes.";
  }
  if (message) {
    snackbar.show(SnackbarState.ERROR, message);
  }
}

async function toggleReviewItemSynthesisAttributeFinalization() {
  if (review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED) {
    return;
  }
  loading.start();
  try {
    if (isFinalized.value) {
      await unfinalizeReviewItemSynthesisAttribute();
    } else {
      await finalizeReviewItemSynthesisAttribute();
    }
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function acceptAiSuggestion(hide: () => void) {
  loading.start();
  try {
    await review.editAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    await finalizeReviewItemSynthesisAttribute();
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function rejectAiSuggestion(hide: () => void) {
  loading.start();
  try {
    await review.clearAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function editAiSuggestion(hide: () => void) {
  loading.start();
  try {
    await review.editAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function finalizeReviewItemSynthesisAttribute() {
  loading.start();
  try {
    await review.finalizeReviewItemSynthesisAttribute(props.study.id, props.attributeStructure.id);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function unfinalizeReviewItemSynthesisAttribute() {
  loading.start();
  try {
    await review.unfinalizeReviewItemSynthesisAttribute(
      props.study.id,
      props.attributeStructure.id,
    );
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

const isVisible = ref(false);
const editorContainer = useTemplateRef("editorContainer");

const finalizeButtonTitle = computed(() => {
  if (isFinalized.value) return "Edit extraction";
  return isAnswerEmpty.value ? "Mark as not reported" : "Confirm extraction";
});

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true;
        observer.disconnect();
      }
    },
    {
      rootMargin: "50px",
      threshold: 0.1,
    },
  );

  if (editorContainer.value) {
    observer.observe(editorContainer.value);
  }
});
</script>
